import axios from "axios";

const API_URL = process.env.VUE_APP_API_BASE_URL + '/';

export default {
  async login(user) {
    try {
      const response = await axios.post(API_URL + "login", user);
      
      if (response.data.access_token) {
        const token = response.data.access_token;
        
        // Guardar el token inmediatamente
        localStorage.setItem("auth_token", token);
        console.log('Token guardado en localStorage:', token.substring(0, 20) + '...');
        
        // Verificar si la respuesta ya incluye datos del usuario
        if (response.data.user) {
          console.log('Datos del usuario incluidos en la respuesta del login');
          
          // Guardar información del usuario
          const userData = response.data.user;
          localStorage.setItem("userF", JSON.stringify(userData));
          
          // Retornar datos estructurados para el store
          return {
            data: {
              user: userData,
              token: token
            }
          };
        } else {
          // Fallback: intentar obtener usuario desde /me
          console.log('Intentando obtener información del usuario desde /me...');
          
          try {
            const userResponse = await axios.get(API_URL + "me", {
              headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json'
              }
            });
            
            console.log('Información del usuario obtenida exitosamente');
            
            // Guardar información del usuario
            const userData = userResponse.data;
            localStorage.setItem("userF", JSON.stringify(userData));
            
            // Retornar datos estructurados para el store
            return {
              data: {
                user: userData,
                token: token
              }
            };
          } catch (userError) {
            console.warn('Error getting user info after login:', userError.message);
            
            // Mantener el token guardado aunque no obtengamos la info del usuario inmediatamente
            return {
              data: {
                user: null,
                token: token
              }
            };
          }
        }
      }
      
      return response;
    } catch (error) {
      // Solo limpiar localStorage si el login falló completamente
      if (!error.config || !error.config.url.includes('/me')) {
        localStorage.removeItem("auth_token");
        localStorage.removeItem("userF");
      }
      throw error;
    }
  },

  async logout() {
    try {
      const token = localStorage.getItem("auth_token");
      if (token) {
        await axios.post(API_URL + "logout", {}, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });
      }
    } catch (error) {
      console.error('Error during logout:', error);
    } finally {
      // Eliminar ambos items del localStorage
      localStorage.removeItem("auth_token");
      localStorage.removeItem("userF");
    }
  },

  async register(user) {
    try {
      const response = await axios.post(API_URL + "register", user);
      
      if (response.data.access_token) {
        const token = response.data.access_token;
        
        // Guardar el token inmediatamente
        localStorage.setItem("auth_token", token);
        
        // Obtener información del usuario con el token recién guardado
        try {
          const userResponse = await axios.get(API_URL + "me", {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          });
          
          // Guardar información del usuario
          const userData = userResponse.data;
          localStorage.setItem("userF", JSON.stringify(userData));
          
          // Retornar datos estructurados para el store
          return {
            data: {
              user: userData,
              token: token
            }
          };
        } catch (userError) {
          console.error('Error getting user info after register:', userError);
          
          // Si falla obtener info del usuario, mantener el registro pero sin datos de usuario
          localStorage.setItem("userF", JSON.stringify(token)); // Compatibilidad
          
          return {
            data: {
              user: null,
              token: token
            }
          };
        }
      }
      
      return response;
    } catch (error) {
      // Limpiar cualquier token que pueda haberse guardado
      localStorage.removeItem("auth_token");
      localStorage.removeItem("userF");
      throw error;
    }
  },

  // Método para obtener el usuario actual
  async getCurrentUser() {
    const token = localStorage.getItem("auth_token");
    if (!token) {
      throw new Error('No token found');
    }
    
    const response = await axios.get(API_URL + "me", {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });
    
    return response;
  },

  // Nuevo método para obtener permisos del usuario
  async getUserPermissions() {
    const token = localStorage.getItem("auth_token");
    if (!token) {
      throw new Error('No token found');
    }
    
    try {
      const response = await axios.get(API_URL + "me/permissions", {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      // Verificar que la respuesta tenga el formato esperado
      if (response.data && response.data.data) {
        return response.data.data;
      } else if (response.data && typeof response.data === 'object') {
        // Si los permisos vienen directamente en data
        return response.data;
      } else {
        console.warn('Formato de respuesta de permisos inesperado:', response.data);
        return {};
      }
    } catch (error) {
      console.error('Error getting user permissions:', error);
      
      // Si es error 401 o 403, el token es inválido
      if (error.response && (error.response.status === 401 || error.response.status === 403)) {
        localStorage.removeItem("auth_token");
        localStorage.removeItem("userF");
      }
      
      throw error;
    }
  },

  // Método para actualizar el perfil del usuario
  async updateProfile(profileData) {
    const token = localStorage.getItem("auth_token");
    const response = await axios.put(API_URL + "me", profileData, {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });
    
    return response;
  },

  // Método para refrescar la información del usuario
  async refreshUser() {
    try {
      const userResponse = await this.getCurrentUser();
      const userData = userResponse.data;
      localStorage.setItem("userF", JSON.stringify(userData));
      return userData;
    } catch (error) {
      console.error('Error refreshing user:', error);
      throw error;
    }
  },

  // Método para verificar si el token es válido
  async validateToken() {
    try {
      const token = localStorage.getItem("auth_token");
      if (!token) {
        return false;
      }
      
      const response = await axios.get(API_URL + "me", {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      return response.status === 200;
    } catch (error) {
      return false;
    }
  },

  // Método para verificar si el usuario tiene un permiso específico
  async hasPermission(permission) {
    try {
      const permissions = await this.getUserPermissions();
      return permissions[permission] === true;
    } catch (error) {
      console.error('Error checking permission:', error);
      return false;
    }
  },

  // Método para verificar acceso a módulos específicos
  async hasModuleAccess(moduleId) {
    try {
      const permissions = await this.getUserPermissions();
      return permissions[moduleId] === true;
    } catch (error) {
      console.error('Error checking module access:', error);
      return false;
    }
  }
};